// Glossary data for tooltips and term definitions
export interface TermDefinition {
  term: string;
  definition: string;
  example?: string;
  formula?: string;
  aliases?: string[];
}

export const glossaryData: Record<string, TermDefinition> = {
  // Math & Optimization Terms
  "optimization": {
    term: "Optimization",
    definition: "Finding the best solution from all feasible solutions by maximizing or minimizing an objective function.",
    example: "Finding the price that maximizes profit while maintaining minimum margins."
  },
  "ebitda": {
    term: "EBITDA",
    definition: "Earnings Before Interest, Taxes, Depreciation, and Amortization. A measure of operational profitability.",
    example: "A company with $10M revenue and $6M operating expenses has $4M EBITDA (40% margin)."
  },
  "linear programming": {
    term: "Linear Programming",
    definition: "Optimization where objective and constraints are linear functions of decision variables.",
    aliases: ["LP"],
    example: "Optimizing production quantities where costs are proportional to quantities."
  },
  "mixed-integer programming": {
    term: "Mixed-Integer Programming",
    definition: "Optimization where some variables must be integers (whole numbers) while others can be continuous.",
    aliases: ["MIP", "MILP"],
    example: "Deciding how many facilities to open (integer) and production at each (continuous)."
  },
  "nonlinear programming": {
    term: "Nonlinear Programming",
    definition: "Optimization with nonlinear objective or constraints (squares, exponentials, products).",
    aliases: ["NLP"],
    example: "Pricing where demand = a × price^(-elasticity) is nonlinear."
  },
  "dynamic programming": {
    term: "Dynamic Programming",
    definition: "Breaking complex problems into simpler subproblems and storing solutions to avoid redundant calculations.",
    aliases: ["DP"],
    example: "Optimal payment schedule where each payment affects future cash availability."
  },
  "monte carlo simulation": {
    term: "Monte Carlo Simulation",
    definition: "Using repeated random sampling to obtain numerical results and assess risk.",
    aliases: ["MC Simulation"],
    example: "Simulating 10,000 scenarios of prices and costs to assess investment risk."
  },
  "differential evolution": {
    term: "Differential Evolution",
    definition: "Population-based optimization algorithm using mutation, crossover, and selection.",
    aliases: ["DE"],
    example: "Finding optimal prices for 2,500 products simultaneously."
  },
  "elasticity": {
    term: "Price Elasticity of Demand",
    definition: "Measure of how responsive demand is to price changes: % change in quantity / % change in price.",
    aliases: ["Demand Elasticity"],
    example: "Elasticity of -2.5 means 10% price increase causes 25% demand decrease.",
    formula: "ε = (∂Q/∂P) × (P/Q)"
  },
  "eoq": {
    term: "Economic Order Quantity",
    definition: "Optimal order quantity that minimizes total ordering and holding costs.",
    aliases: ["EOQ"],
    example: "Order 500 units at a time to minimize costs.",
    formula: "EOQ = √(2DS/H)"
  },
  "arima": {
    term: "ARIMA",
    definition: "AutoRegressive Integrated Moving Average. Time series forecasting method for patterns and trends.",
    example: "Predicting next month's sales based on past 24 months of data."
  },
  "random forest": {
    term: "Random Forest",
    definition: "Machine learning algorithm combining multiple decision trees for improved accuracy.",
    example: "Forecasting demand based on historical sales, seasonality, and promotions."
  },
  "fourier analysis": {
    term: "Fourier Analysis",
    definition: "Decomposing functions into sine/cosine waves to identify periodic patterns.",
    example: "Detecting weekly and monthly sales patterns in transaction data."
  },
  "solver": {
    term: "Solver",
    definition: "Software implementing algorithms to find solutions to optimization problems.",
    example: "CPLEX, Gurobi, IPOPT are popular optimization solvers."
  },
  "ipopt": {
    term: "IPOPT",
    definition: "Interior Point Optimizer. Open-source solver for large-scale nonlinear optimization.",
    example: "Used for tax optimization with nonlinear profit allocation."
  },
  "latin hypercube sampling": {
    term: "Latin Hypercube Sampling",
    definition: "Efficient sampling ensuring better coverage than random sampling by stratifying probability space.",
    aliases: ["LHS"],
    example: "Generating 1,000 diverse scenarios for risk analysis efficiently."
  },
  "regression": {
    term: "Regression Analysis",
    definition: "Statistical method estimating relationships between dependent and independent variables.",
    example: "Estimating demand as function of price, season, and competitor prices.",
    formula: "y = β₀ + β₁x₁ + β₂x₂ + ... + ε"
  },

  // Finance Terms
  "npv": {
    term: "Net Present Value",
    definition: "Difference between present value of cash inflows and outflows, used to evaluate investments.",
    aliases: ["NPV"],
    example: "Investment with $100K cost and $120K PV of returns has NPV = $20K.",
    formula: "NPV = Σ(CF_t / (1+r)^t) - Initial Investment"
  },
  "irr": {
    term: "Internal Rate of Return",
    definition: "Discount rate making NPV zero; represents expected annual return of investment.",
    aliases: ["IRR"],
    example: "Project with 15% IRR generates 15% annual returns.",
    formula: "0 = Σ(CF_t / (1+IRR)^t)"
  },
  "roi": {
    term: "Return on Investment",
    definition: "Performance measure: net profit divided by investment cost as percentage.",
    aliases: ["ROI"],
    example: "Investing $100K to save $25K annually gives 25% ROI.",
    formula: "ROI = (Gain - Cost) / Cost × 100%"
  },
  "etr": {
    term: "Effective Tax Rate",
    definition: "Average rate at which corporate income is taxed: total tax / pre-tax income.",
    aliases: ["ETR"],
    example: "Paying $28.5M tax on $100M pre-tax income gives 28.5% ETR.",
    formula: "ETR = Total Tax / Pre-Tax Income × 100%"
  },
  "working capital": {
    term: "Working Capital",
    definition: "Current assets minus current liabilities; liquidity for day-to-day operations.",
    example: "$50M current assets - $30M current liabilities = $20M working capital."
  },
  "dpo": {
    term: "Days Payable Outstanding",
    definition: "Average days a company takes to pay suppliers.",
    aliases: ["DPO", "Payables Days"],
    example: "DPO of 45 days means paying suppliers 45 days after invoices.",
    formula: "DPO = (Accounts Payable / COGS) × 365"
  },
  "dso": {
    term: "Days Sales Outstanding",
    definition: "Average days to collect payment after sale.",
    aliases: ["DSO", "Receivables Days"],
    example: "DSO of 30 days means customers pay 30 days after sale.",
    formula: "DSO = (Accounts Receivable / Revenue) × 365"
  },
  "dio": {
    term: "Days Inventory Outstanding",
    definition: "Average days inventory is held before being sold.",
    aliases: ["DIO"],
    example: "DIO of 60 days means inventory sits 60 days before sale.",
    formula: "DIO = (Inventory / COGS) × 365"
  },
  "gross margin": {
    term: "Gross Margin",
    definition: "Revenue minus cost of goods sold, divided by revenue.",
    example: "Selling for $100 with $70 cost gives 30% gross margin.",
    formula: "Gross Margin = (Revenue - COGS) / Revenue × 100%"
  },
  "var": {
    term: "Value at Risk",
    definition: "Maximum expected loss over time period at given confidence level.",
    aliases: ["VaR"],
    example: "95% VaR of $5M means 5% chance of losing more than $5M.",
    formula: "P(Loss > VaR) = α (e.g., 5%)"
  },
  "cvar": {
    term: "Conditional Value at Risk",
    definition: "Expected loss given that loss exceeds VaR threshold; measures tail risk.",
    aliases: ["CVaR", "Expected Shortfall"],
    example: "If 95% VaR is $5M and CVaR is $8M, losses beyond VaR average $8M."
  },
  "wacc": {
    term: "Weighted Average Cost of Capital",
    definition: "Average rate company pays for capital from all sources, weighted by proportions.",
    aliases: ["WACC"],
    example: "60% equity at 12% + 40% debt at 6% = 9.6% WACC.",
    formula: "WACC = (E/V)R_e + (D/V)R_d(1-T)"
  },
  "beps": {
    term: "Base Erosion and Profit Shifting",
    definition: "Tax strategies shifting profits to low-tax jurisdictions; increasingly regulated.",
    aliases: ["BEPS"],
    example: "Allocating IP to Ireland (12.5% tax) while operating in US (21% tax)."
  },
  "transfer pricing": {
    term: "Transfer Pricing",
    definition: "Prices for goods/services between related entities, subject to arm's length requirements.",
    example: "US parent charging Irish subsidiary 10% royalty for IP."
  },
  "arm's length": {
    term: "Arm's Length Principle",
    definition: "Intercompany transactions must be priced as if between unrelated parties.",
    example: "IP royalties must be within 8-15% based on comparable transactions."
  },
  "fill rate": {
    term: "Fill Rate",
    definition: "Percentage of orders fulfilled immediately from available inventory.",
    aliases: ["Service Level"],
    example: "98% fill rate means 98 of 100 orders fulfilled without stockouts.",
    formula: "Fill Rate = Orders Fulfilled / Total Orders × 100%"
  },
  "inventory turnover": {
    term: "Inventory Turnover",
    definition: "Number of times inventory is sold and replaced over a period.",
    aliases: ["Inventory Turns"],
    example: "6x turnover means selling entire inventory 6 times per year.",
    formula: "Inventory Turnover = COGS / Average Inventory"
  },
  "carrying cost": {
    term: "Carrying Cost",
    definition: "Total cost of holding inventory: storage, insurance, depreciation, opportunity cost.",
    aliases: ["Holding Cost"],
    example: "Typical carrying costs are 20-30% of inventory value annually."
  },
  "safety stock": {
    term: "Safety Stock",
    definition: "Extra inventory held to protect against demand variability and supply disruptions.",
    aliases: ["Buffer Stock"],
    example: "Holding 2 weeks safety stock to achieve 98% service level.",
    formula: "Safety Stock = Z × σ × √LT"
  },
  "cogs": {
    term: "Cost of Goods Sold",
    definition: "Direct costs attributable to production of goods sold.",
    aliases: ["COGS"],
    example: "For retailers, includes wholesale price and freight costs.",
    formula: "COGS = Beginning Inventory + Purchases - Ending Inventory"
  },

  // Technical Terms
  "api": {
    term: "API",
    definition: "Application Programming Interface. Rules allowing software applications to communicate.",
    example: "Optimization API accepts parameters and returns optimal prices."
  },
  "sku": {
    term: "SKU",
    definition: "Stock Keeping Unit. Unique identifier for each distinct purchasable product.",
    example: "SKU-LAPTOP-15-BLK identifies a specific laptop model."
  },
  "ml": {
    term: "Machine Learning",
    definition: "Algorithms learning patterns from data to make predictions without explicit programming.",
    aliases: ["ML", "AI"],
    example: "Using ML to forecast demand based on historical data and patterns."
  },
  "kpi": {
    term: "Key Performance Indicator",
    definition: "Measurable value demonstrating how effectively objectives are achieved.",
    aliases: ["KPI"],
    example: "Revenue growth, gross margin, inventory turnover are common KPIs."
  }
};

// Helper function to find term definition (case-insensitive)
export function getTermDefinition(term: string): TermDefinition | undefined {
  const normalizedTerm = term.toLowerCase().trim();

  // Direct lookup
  if (glossaryData[normalizedTerm]) {
    return glossaryData[normalizedTerm];
  }

  // Search by aliases
  for (const key in glossaryData) {
    const entry = glossaryData[key];
    if (entry.aliases?.some(alias => alias.toLowerCase() === normalizedTerm)) {
      return entry;
    }
  }

  return undefined;
}

// Extract terms from text that have definitions
export function extractDefinedTerms(text: string): string[] {
  const terms: string[] = [];
  const words = text.split(/\b/);

  // Check for multi-word terms first (up to 4 words)
  for (let i = 0; i < words.length; i++) {
    for (let len = 4; len >= 1; len--) {
      const phrase = words.slice(i, i + len).join('').toLowerCase().trim();
      if (getTermDefinition(phrase)) {
        terms.push(words.slice(i, i + len).join(''));
        i += len - 1;
        break;
      }
    }
  }

  return terms;
}
